import { useState, useEffect } from "react";
import axios from "axios";
import Detailtable from "../Table/detailTable";
import Footer from "../Footer";
import Navbar from "../Navbar";
import { useLocation } from "react-router-dom";
import { formatSalesValue } from './../../utils/formatUtils';
import { FiPrinter, FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";

const Detail = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [debitQty, setDebitQty] = useState(0);
  const [debitAmount, setDebitAmount] = useState("");
  const [debitNotes, setDebitNotes] = useState([]);
  const [hasDebitNote, setHasDebitNote] = useState(false);
  const [itemMetaMap, setItemMetaMap] = useState({});
  const [deleting, setDeleting] = useState(false);

  const { state } = useLocation();

  const {
    ID,
    date,
    companyName,
    outletName,
    status,
    billNum,
    discount,
    tax,
    total,
    GRN,
    startDate,
    endDate,
    viewMode,
    purchase_from
  } = state || {};

  let length = list.length;

  const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);

    if (isNaN(d.getTime())) return "";

    return new Intl.DateTimeFormat("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(d);
  };

  useEffect(() => {
    if (!ID) return;
    getItemDetail();
  }, [ID]);

  useEffect(() => {
    localStorage.setItem("startDate", JSON.stringify(startDate));
    localStorage.setItem("endDate", JSON.stringify(endDate));
  }, [startDate, endDate, viewMode])

  const getItemDetail = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await axios.get(`${baseUrl}/reqdetails/${ID}`);
      const data = result.data;

      setList(data.intbl_purchaserequisition_contract || []);

      const details = data.purchase_requisition_details || {};

      setDebitQty(Number(details.total_debit_quantity) || 0);
      setHasDebitNote(details.has_debitnote || false);
      setDebitAmount(details.total_debit_amount || "");

      setDebitNotes(details.debitnotes || []);

      const contractItems = data.intbl_purchaserequisition_contract || [];
      setList(contractItems);

      const map = {};
      contractItems.forEach(item => {
        map[item.ItemID] = {
          taxable: item.Taxable,
          brand: item.BrandName
        };
      });

      setItemMetaMap(map);

    } catch (error) {
      console.log(error);
      setError("Failed to load item details.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    toast.info(
      ({ closeToast }) => (
        <div>
          <p className="mb-2 text-sm">Are you sure you want to delete this record?</p>
          <div className="flex gap-2">
            <button
              onClick={async () => {
                closeToast();
                try {
                  setDeleting(true);

                  await axios.put(`${baseUrl}/deleteinventory_web`, {
                    PurchaseReqID: ID,
                    outletName: outletName,
                  });

                  toast.success("Record deleted successfully");
                  window.history.back();

                } catch (err) {
                  console.error(err);
                  toast.error("Failed to delete record");
                } finally {
                  setDeleting(false);
                }
              }}
              className="px-3 py-1 bg-red-500 text-white rounded text-xs"
            >
              Yes, Delete
            </button>

            <button
              onClick={closeToast}
              className="px-3 py-1 bg-gray-200 rounded text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
      }
    );
  };

  const finalTotal = Number(total) - Number(debitAmount);

  const formatDateTime = (date) => {
    if (!date) return "";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(new Date(date));
  };

  const triggerPrint = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US');
    const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

    const itemRows = list.map((item, index) => {
      const qty = parseFloat(item.UnitsOrdered) || 0;
      const rate = parseFloat(item.Rate) || 0;
      const subtotal = qty * rate;
      const taxable = item.Taxable === "Yes";
      const tax = taxable ? (subtotal * 0.13).toFixed(2) : '0.00';
      const total = taxable ? (subtotal * 1.13).toFixed(2) : subtotal.toFixed(2);

      return `
            <tr>
                <td>${index + 1}</td>
                <td>${item.Name || item.ItemName || ''}</td>
                <td>${item.BrandName || '-'}</td>
                <td>${item.StockType || '-'}</td>
                <td>${item.UOM || '-'}</td>
                <td class="right">${qty.toFixed(2)}</td>
                <td class="right">${rate.toFixed(2)}</td>
                <td class="right">${tax}</td>
                <td class="right">${total}</td>
            </tr>
        `;
    }).join('');

    const debitNoteSection = (hasDebitNote && debitNotes.length > 0) ? `
        <div class="section-title" style="margin-top:24px;">Debit Notes (${formatDateTime(debitNotes[0]?.updated_at)})</div>
        <table>
            <thead>
                <tr>
                    <th>S.N</th>
                    <th>Item Name</th>
                    <th>Brand</th>
                    <th>Unit</th>
                    <th class="right">Qty</th>
                    <th class="right">Rate</th>
                    <th class="right">Total</th>
                    <th>Taxable</th>
                </tr>
            </thead>
            <tbody>
                ${debitNotes[0].details?.map((note, index) => {
      const meta = itemMetaMap[note.ItemID] || {};
      return `
                    <tr>
                        <td>${index + 1}</td>
                        <td>${note.ItemName}</td>
                        <td>${meta.brand || '-'}</td>
                        <td>${note.uom || '-'}</td>
                        <td class="right">-${note.Quantity}</td>
                        <td class="right">${note.unit_price || '-'}</td>
                        <td class="right">-${note.total_amount || '-'}</td>
                        <td>${meta.taxable || '-'}</td>
                    </tr>
                  `;
    }).join('')}
            </tbody>
        </table>
        <div class="debit-summary">
            <span>Total Debit Qty: <strong>${debitQty}</strong></span>
            <span>Total Debit Amount: <strong>-${debitAmount}</strong></span>
        </div>
    ` : '';

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Purchase Detail - GRN #${GRN}</title>
            <style>
                *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

                html, body {
                    width: 100%;
                    height: 100%;
                    font-family: 'Courier New', Courier, monospace;
                    font-size: 13px;
                    background: white;
                    color: #000;
                }

                .page {
                    width: 100%;
                    min-height: 100vh;
                    padding: 24px 32px;
                    display: flex;
                    flex-direction: column;
                }

                .header {
                    text-align: center;
                    font-size: 17px;
                    font-weight: bold;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                    margin-bottom: 4px;
                }

                .sub-header {
                    text-align: center;
                    font-size: 13px;
                    margin-bottom: 16px;
                    color: #333;
                }

                .meta-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 4px 16px;
                    margin-bottom: 16px;
                    font-size: 13px;
                }

                .meta-item { display: flex; gap: 6px; }
                .meta-item .label { font-weight: 700; white-space: nowrap; }
                .meta-item .value { color: #111; }

                .section-title {
                    font-size: 13px;
                    font-weight: bold;
                    margin-bottom: 6px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                hr.divider { border: none; border-top: 1px dashed #000; margin: 10px 0; }
                hr.divider-solid { border: none; border-top: 2px solid #000; margin: 10px 0; }

                table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 12px;
                }

                thead th {
                    text-align: left;
                    padding: 5px 6px;
                    border-top: 1px solid #000;
                    border-bottom: 1px solid #000;
                    white-space: nowrap;
                    font-size: 12px;
                }

                tbody td {
                    padding: 4px 6px;
                    vertical-align: top;
                    word-break: break-word;
                }

                tbody tr:not(:last-child) td { border-bottom: 1px dotted #ccc; }

                th.right, td.right { text-align: right; }

                .summary-block {
                    margin-top: 12px;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 4px;
                    font-size: 13px;
                }

                .summary-row {
                    display: flex;
                    gap: 16px;
                    justify-content: flex-end;
                }

                .summary-row .s-label { font-weight: 600; text-align: right; min-width: 120px; }
                .summary-row .s-value { text-align: right; min-width: 80px; }

                .grand-total .s-label,
                .grand-total .s-value {
                    font-size: 15px;
                    font-weight: bold;
                    border-top: 1px solid #000;
                    padding-top: 4px;
                    margin-top: 4px;
                }

                .debit-summary {
                    display: flex;
                    justify-content: flex-end;
                    gap: 24px;
                    margin-top: 10px;
                    font-size: 13px;
                    color: #b91c1c;
                }

                .footer {
                    margin-top: auto;
                    padding-top: 24px;
                    font-size: 13px;
                }

                .count-row {
                    display: flex;
                    justify-content: space-between;
                    margin-top: 8px;
                    font-size: 12px;
                    color: #444;
                }

                .print-btn {
                    display: block;
                    margin: 16px auto 0;
                    padding: 8px 24px;
                    font-size: 14px;
                    cursor: pointer;
                    background: #1a56db;
                    color: white;
                    border: none;
                    border-radius: 4px;
                }

                @media print {
                    html, body { font-size: 12px; }
                    .page { padding: 12px 16px; min-height: 100vh; }
                    .print-btn { display: none !important; }
                    table { font-size: 11px; }
                    @page { size: auto; margin: 10mm; }
                }
            </style>
        </head>
        <body>
            <div class="page">
                <div class="header">Central Store Purchase Record</div>
                <div class="sub-header">${outletName}</div>

                <div class="meta-grid">
                    <div class="meta-item"><span class="label">GRN #:</span><span class="value">${GRN}</span></div>
                    <div class="meta-item"><span class="label">Date:</span><span class="value">${formatDate(date)}</span></div>
                    <div class="meta-item"><span class="label">Print Date:</span><span class="value">${dateStr} ${timeStr}</span></div>
                    <div class="meta-item"><span class="label">Vendor:</span><span class="value">${companyName}</span></div>
                    <div class="meta-item"><span class="label">Bill No:</span><span class="value">${billNum}</span></div>
                    <div class="meta-item"><span class="label">Status:</span><span class="value">${status}</span></div>
                </div>

                <hr class="divider-solid" />

                <div class="section-title">Items</div>
                <table>
                    <thead>
                        <tr>
                            <th>S.N</th>
                            <th>Item Name</th>
                            <th>Brand</th>
                            <th>Stock Type</th>
                            <th>Unit</th>
                            <th class="right">Qty</th>
                            <th class="right">Rate</th>
                            <th class="right">Tax (13%)</th>
                            <th class="right">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${itemRows}
                    </tbody>
                </table>

                <hr class="divider-solid" />

                ${debitNoteSection}

                <hr class="divider-solid" />

                <div class="count-row">
                    <span>Item Count: <strong>${list.length}</strong></span>
                </div>

                <div class="summary-block">
                    <div class="summary-row">
                        <span class="s-label">Discount:</span>
                        <span class="s-value">${formatSalesValue(discount)}</span>
                    </div>
                    <div class="summary-row">
                        <span class="s-label">Tax Amount:</span>
                        <span class="s-value">${formatSalesValue(tax)}</span>
                    </div>
                    ${hasDebitNote ? `
                    <div class="summary-row" style="color:#b91c1c;">
                        <span class="s-label">Debit Amount:</span>
                        <span class="s-value">-${debitAmount}</span>
                    </div>` : ''}
                    <div class="summary-row grand-total">
                        <span class="s-label">Grand Total:</span>
                        <span class="s-value">${formatSalesValue(finalTotal)}</span>
                    </div>
                </div>

                <hr class="divider" />

                <div class="footer">
                    <span><strong>FOR:</strong> ${outletName}</span>
                </div>

                <button class="print-btn" onclick="window.print()">🖨 Print</button>
            </div>

            <script>setTimeout(() => window.print(), 600);</script>
        </body>
        </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      <div className="flex-grow">
        <div className="relative max-w-7xl mx-auto p-4">

          {/* Action Buttons */}
          <div className="absolute top-6 right-5 flex items-center gap-2">
            <button
              onClick={triggerPrint}
              className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md text-xs font-semibold transition"
            >
              <FiPrinter size={14} />
              Print
            </button>

            {purchase_from === "web" && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-md text-xs font-semibold transition disabled:opacity-50"
              >
                <FiTrash2 size={14} />
                {deleting ? "Deleting..." : "Delete"}
              </button>
            )}
          </div>

          {/* Page Title */}
          <h1 className="text-xl font-semibold font-poppins text-center text-gray-800 mb-3">
            {outletName}
          </h1>

          {/* Header Card */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-4 mb-8 grid grid-cols-3 items-center text-sm md:text-base">

            {/* Left - Company Info */}
            <div className="space-y-2">
              <p className="font-semibold text-gray-600">
                Company Name:
                <span className="ml-2 font-normal text-gray-800">
                  {companyName}
                </span>
              </p>
              <p className="font-semibold text-gray-600">
                Date:
                <span className="ml-2 font-normal text-gray-800">
                  {formatDate(date)}
                </span>
              </p>
            </div>

            {/* Center - GRN */}
            <div className="text-center">
              <p className="font-semibold text-gray-600">
                GRN:
                <span className="ml-2 font-normal text-gray-800">
                  {GRN}
                </span>
              </p>
            </div>

            {/* Right - Purchase Bill & Status */}
            <div className="space-y-2 text-right">
              <p className="font-semibold text-gray-600">
                Purchase Bill No:
                <span className="ml-2 font-normal text-gray-800">
                  {billNum}
                </span>
              </p>
              <p className="font-semibold text-gray-600">
                Status:
                <span className="ml-2 px-3 py-1 text-xs md:text-sm font-semibold rounded-full bg-green-100 text-green-700">
                  {status}
                </span>
              </p>
            </div>
          </div>

          <Detailtable
            itemList={list}
            outletName={outletName}
            id={ID}
            loading={loading}
            error={error}
          />

          {hasDebitNote && debitNotes.length > 0 && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 mt-6">

              <h2 className="text-sm font-semibold text-gray-700 mb-3">
                <h2 className="text-sm font-semibold text-gray-700 mb-3">
                  Debit Notes ({formatDateTime(debitNotes[0]?.updated_at)})
                </h2>
              </h2>

              <div className="overflow-auto">
                <table className="min-w-full text-sm text-center border border-red-100">
                  <thead className="bg-red-100 text-gray-800">
                    <tr>
                      <th className="px-3 py-2">S.N</th>
                      <th className="px-3 py-2">Item Name</th>
                      <th className="px-3 py-2">Brand</th>
                      <th className="px-3 py-2">Quantity</th>
                      <th className="px-3 py-2">UOM</th>
                      <th className="px-3 py-2">Rate</th>
                      <th className="px-3 py-2">Total</th>
                      <th className="px-3 py-2">Taxable</th>
                    </tr>
                  </thead>

                  <tbody>
                    {debitNotes[0].details?.map((note, index) => {
                      const meta = itemMetaMap[note.ItemID] || {};

                      return (
                        <tr key={note.id} className="border-t">
                          <td className="px-3 py-2">{index + 1}</td>

                          <td className="px-3 py-2 font-semibold">
                            {note.ItemName}
                          </td>

                          <td className="px-3 py-2">
                            {meta.brand || "-"}
                          </td>

                          <td className="px-3 py-2 font-semibold">
                            -{note.Quantity}
                          </td>

                          <td className="px-3 py-2">
                            {note.uom || "-"}
                          </td>

                          <td className="px-3 py-2">
                            {note.unit_price || "-"}
                          </td>

                          <td className="px-3 py-2 font-semibold">
                            -{note.total_amount || "-"}
                          </td>

                          <td className="px-3 py-2">
                            {meta.taxable || "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Total Debit */}
              <div className="flex justify-end gap-5 mt-4 text-sm font-semibold text-gray-800">
                <span>
                  Total Debit Qty: {debitQty}
                </span>
                <span>
                  Total Debit Amount: -{debitAmount}
                </span>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 flex flex-col md:flex-row md:justify-between md:items-center gap-6 text-sm md:text-base mt-8">
            <div className="font-semibold text-gray-700">
              Count:
              <span className="ml-2 font-normal text-gray-900">
                {length}
              </span>
            </div>

            <div className="flex flex-wrap gap-6 font-medium text-gray-700">
              <div>
                Discount:
                <span className="ml-2 font-semibold text-green-600">
                  {formatSalesValue(discount)}
                </span>
              </div>

              <div>
                Tax Amount:
                <span className="ml-2 font-semibold text-green-600">
                  {formatSalesValue(tax)}
                </span>
              </div>

              <div>
                Total:
                <span className="ml-2 font-semibold text-green-600">
                  {formatSalesValue(finalTotal)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Detail;
